import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Logo from "./Logo";
import LanguageSelector from "./LanguageSelector";
import { contact } from "../content/contact";
import "../styles/footer.css";

const SERVICES = [
  { href: "/#services", key: "footer.serviceFinance" },
  { href: "/#services", key: "footer.serviceLogistics" },
  { href: "/#services", key: "footer.serviceAi" },
] as const;

const COMPANY = [
  { href: "/#team", key: "footer.team" },
  { href: "/#approach", key: "footer.approach" },
  { href: "/careers", key: "footer.careers" },
  { href: "/#faq", key: "footer.faq" },
] as const;

/**
 * Dense footer on navy (spec c.7 §10): inverse logo with the one-line
 * descriptor and the language control, Services, Company, a named contact with
 * visible email and phone, staff sign in, then copyright and legal links.
 */
const Footer: React.FC = () => {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  const role = t(contact.roleKey, { defaultValue: "" });

  return (
    <footer className="site-footer">
      <div className="container">
        <div className="footer-grid">
          <div className="footer-brand">
            <Link className="footer-logo" to="/" aria-label={t("header.homeAriaLabel")}>
              <Logo inverse />
            </Link>
            <p className="t-small footer-desc">{t("footer.description")}</p>
            <LanguageSelector inverse className="footer-lang" />
          </div>

          <nav className="footer-col footer-services" aria-labelledby="footer-services-title">
            <h2 id="footer-services-title" className="footer-title">
              {t("footer.servicesTitle")}
            </h2>
            <ul className="footer-links">
              {SERVICES.map((l) => (
                <li key={l.key}>
                  <a className="footer-link" href={l.href}>
                    {t(l.key)}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <nav className="footer-col footer-company" aria-labelledby="footer-company-title">
            <h2 id="footer-company-title" className="footer-title">
              {t("footer.companyTitle")}
            </h2>
            <ul className="footer-links">
              {COMPANY.map((l) => (
                <li key={l.key}>
                  <a className="footer-link" href={l.href}>
                    {t(l.key)}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="footer-col footer-contact">
            <h2 className="footer-title">{t("footer.contactTitle")}</h2>
            <p className="footer-person">
              <span className="footer-name">{contact.name}</span>
              {role && <span className="footer-role">{role}</span>}
            </p>
            <ul className="footer-links">
              <li>
                <a className="footer-link" href={`mailto:${contact.email}`}>
                  {contact.email}
                </a>
              </li>
              <li>
                <a className="footer-link footer-tel" href={`tel:${contact.phone}`}>
                  {contact.phoneDisplay}
                </a>
              </li>
              <li>
                <a className="footer-link" href={`mailto:${contact.careersEmail}`}>
                  <span className="footer-link-meta">{t("footer.careersEmail")}</span>
                  {contact.careersEmail}
                </a>
              </li>
              <li>
                <a className="footer-link" href="/admin/login">
                  {t("footer.signIn")}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <p className="footer-copy">
            {t("footer.copyright", { year })}
            {contact.legalEntity ? ` ${contact.legalEntity}` : ""}
          </p>
          <nav className="footer-legal" aria-label={t("footer.legalAriaLabel")}>
            <Link className="footer-link" to="/privacy">
              {t("footer.privacy")}
            </Link>
            <Link className="footer-link" to="/terms">
              {t("footer.terms")}
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
